import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const PermissionMatrix = ({ 
  role, 
  permissionModules, 
  onPermissionChange, 
  onBulkAssignment 
}) => {
  const [expandedModules, setExpandedModules] = useState({});
  const [bulkMode, setBulkMode] = useState(false);

  const toggleModule = (moduleId) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  const handleModuleToggle = (moduleId, enabled) => {
    const permissions = enabled 
      ? { create: true, read: true, update: true, delete: true }
      : { create: false, read: false, update: false, delete: false };
    
    onBulkAssignment(moduleId, permissions);
  };

  const getModulePermissionCount = (moduleId) => {
    const modulePermissions = role.permissions[moduleId];
    return Object.values(modulePermissions).filter(Boolean).length;
  };

  const isModuleFullyEnabled = (moduleId) => {
    const modulePermissions = role.permissions[moduleId];
    return Object.values(modulePermissions).every(Boolean);
  };

  const isModulePartiallyEnabled = (moduleId) => {
    const modulePermissions = role.permissions[moduleId];
    const enabledCount = Object.values(modulePermissions).filter(Boolean).length;
    return enabledCount > 0 && enabledCount < Object.keys(modulePermissions).length;
  };

  const getPermissionIcon = (permissionKey) => {
    switch (permissionKey) {
      case 'create': return 'Plus';
      case 'read': return 'Eye';
      case 'update': return 'Edit';
      case 'delete': return 'Trash2';
      default: return 'Shield';
    }
  };

  const getPermissionColor = (permissionKey, enabled) => {
    if (!enabled) return 'text-secondary-400';
    
    switch (permissionKey) {
      case 'create': return 'text-success';
      case 'read': return 'text-primary';
      case 'update': return 'text-accent';
      case 'delete': return 'text-error';
      default: return 'text-secondary-500';
    }
  };

  const renderBulkActions = () => (
    <div className="bg-secondary-50 rounded-lg p-4 mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium text-text-primary">Bulk Permission Assignment</h3>
        <button
          onClick={() => setBulkMode(!bulkMode)}
          className={`
            px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-150
            ${bulkMode 
              ? 'bg-primary text-white' :'border border-secondary-300 text-text-primary hover:bg-secondary-50'
            }
          `}
        >
          {bulkMode ? 'Exit Bulk Mode' : 'Enable Bulk Mode'}
        </button>
      </div>
      
      {bulkMode && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <button
            onClick={() => {
              permissionModules.forEach(module => {
                onBulkAssignment(module.id, { create: true, read: true, update: true, delete: true });
              });
            }}
            className="
              bg-success text-white px-3 py-2 rounded-md text-sm font-medium
              hover:bg-success-600 transition-colors duration-150
              flex items-center justify-center gap-1
            "
          >
            <Icon name="CheckCircle" size={14} />
            All Permissions
          </button>
          
          <button
            onClick={() => {
              permissionModules.forEach(module => {
                onBulkAssignment(module.id, { create: false, read: true, update: false, delete: false });
              });
            }}
            className="
              bg-primary text-white px-3 py-2 rounded-md text-sm font-medium
              hover:bg-primary-600 transition-colors duration-150
              flex items-center justify-center gap-1
            "
          >
            <Icon name="Eye" size={14} />
            Read Only
          </button>
          
          <button
            onClick={() => {
              permissionModules.forEach(module => {
                onBulkAssignment(module.id, { create: true, read: true, update: true, delete: false });
              });
            }}
            className="
              bg-accent text-white px-3 py-2 rounded-md text-sm font-medium
              hover:bg-accent-600 transition-colors duration-150
              flex items-center justify-center gap-1
            "
          >
            <Icon name="Edit" size={14} />
            Editor
          </button>
          
          <button
            onClick={() => {
              permissionModules.forEach(module => {
                onBulkAssignment(module.id, { create: false, read: false, update: false, delete: false });
              });
            }}
            className="
              bg-error text-white px-3 py-2 rounded-md text-sm font-medium
              hover:bg-error-600 transition-colors duration-150
              flex items-center justify-center gap-1
            "
          >
            <Icon name="XCircle" size={14} />
            No Access
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-text-primary">Permission Matrix</h3>
        <div className="text-sm text-text-secondary">
          {Object.values(role.permissions).reduce((count, module) => 
            count + Object.values(module).filter(Boolean).length, 0
          )} permissions enabled
        </div>
      </div>

      {renderBulkActions()}

      <div className="space-y-4">
        {permissionModules.map(module => {
          const isExpanded = expandedModules[module.id] !== false; // Default to expanded
          const permissionCount = getModulePermissionCount(module.id);
          const isFullyEnabled = isModuleFullyEnabled(module.id);
          const isPartiallyEnabled = isModulePartiallyEnabled(module.id);

          return (
            <div key={module.id} className="bg-surface rounded-lg border border-secondary-200">
              {/* Module Header */}
              <div 
                className="p-4 cursor-pointer hover:bg-secondary-25 transition-colors duration-150"
                onClick={() => toggleModule(module.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-md bg-secondary-100`}>
                      <Icon name={module.icon} size={20} className="text-secondary-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-text-primary">{module.name}</h4>
                      <p className="text-sm text-text-secondary">{module.description}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Permission Status */}
                    <div className="flex items-center gap-2">
                      {isFullyEnabled && (
                        <div className="flex items-center gap-1 text-success text-sm">
                          <Icon name="CheckCircle" size={14} />
                          <span>Full Access</span>
                        </div>
                      )}
                      {isPartiallyEnabled && (
                        <div className="flex items-center gap-1 text-warning text-sm">
                          <Icon name="AlertCircle" size={14} />
                          <span>{permissionCount} of 4</span>
                        </div>
                      )}
                      {!isFullyEnabled && !isPartiallyEnabled && (
                        <div className="flex items-center gap-1 text-secondary-400 text-sm">
                          <Icon name="XCircle" size={14} />
                          <span>No Access</span>
                        </div>
                      )}
                    </div>

                    {/* Module Toggle */}
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isFullyEnabled}
                        onChange={(e) => handleModuleToggle(module.id, e.target.checked)}
                        className="sr-only"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className={`
                        relative w-10 h-5 rounded-full transition-colors duration-200
                        ${isFullyEnabled ? 'bg-success' : 'bg-secondary-300'}
                      `}>
                        <div className={`
                          absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform duration-200
                          ${isFullyEnabled ? 'translate-x-5' : 'translate-x-0'}
                        `} />
                      </div>
                    </label>

                    {/* Expand/Collapse */}
                    <Icon 
                      name={isExpanded ? 'ChevronUp' : 'ChevronDown'} 
                      size={16} 
                      className="text-secondary-400" 
                    />
                  </div>
                </div>
              </div>

              {/* Permission Details */}
              {isExpanded && (
                <div className="border-t border-secondary-200 p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {module.permissions.map(permission => {
                      const isEnabled = role.permissions[module.id][permission.key];
                      
                      return (
                        <div key={permission.key} className="flex items-center justify-between p-3 bg-background rounded-md">
                          <div className="flex items-center gap-3">
                            <div className={`p-1.5 rounded-md bg-secondary-100`}>
                              <Icon 
                                name={getPermissionIcon(permission.key)} 
                                size={14} 
                                className={getPermissionColor(permission.key, isEnabled)}
                              />
                            </div>
                            <div>
                              <p className="font-medium text-text-primary text-sm">
                                {permission.label}
                              </p>
                              <p className="text-xs text-text-secondary">
                                {permission.description}
                              </p>
                            </div>
                          </div>

                          <label className="flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={isEnabled}
                              onChange={(e) => onPermissionChange(module.id, permission.key, e.target.checked)}
                              className="sr-only"
                            />
                            <div className={`
                              relative w-8 h-4 rounded-full transition-colors duration-200
                              ${isEnabled ? 'bg-primary' : 'bg-secondary-300'}
                            `}>
                              <div className={`
                                absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full transition-transform duration-200
                                ${isEnabled ? 'translate-x-4' : 'translate-x-0'}
                              `} />
                            </div>
                          </label>
                        </div>
                      );
                    })}
                  </div>

                  {/* Permission Inheritance Info */}
                  {role.parentId && (
                    <div className="mt-4 p-3 bg-accent-50 border border-accent-200 rounded-md">
                      <div className="flex items-start gap-2">
                        <Icon name="Info" size={16} className="text-accent mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-accent">
                            Permission Inheritance
                          </p>
                          <p className="text-xs text-accent-700 mt-1">
                            Some permissions may be inherited from parent roles and cannot be modified directly.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PermissionMatrix;